import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  CardTitle,
  Col,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
  Spinner,
  Table,
} from "reactstrap";
import DepositoBoletaForm from "./DepositoBoletaForm";
import { CajaDiaAnterior, SucursalRendicion } from "../../RendicionCaja/types";
import { FilaPorFormaPago, FilaReporteTesoreria, RendicionEstadoCaja } from "../types";
import { requestJson } from "../../../../helpers/api";

interface ControlCajaPanelProps {
  filasPorFormaPago: FilaPorFormaPago[];
  filasReporte: FilaReporteTesoreria[];
  cargando: boolean;
  error: string | null;
  onReload: () => void;
  onAgregarFormaPago?: (contexto: GrupoContexto) => void;
  onVerFacturas?: (contexto: GrupoContexto) => void;
  onEliminarFormaManual?: (fila: FilaPorFormaPago) => void;
  rendicionesPorCaja?: Record<number, RendicionEstadoCaja>;
  puedeConciliarSupervisor: boolean;
  puedeConciliarTesoreria: boolean;
  onActualizarConciliacion?: (accion: {
    etapa: "supervisor" | "tesoreria";
    estado: "aprobada" | "rechazada" | "pendiente";
    nota?: string;
    rendicionId: number;
  }) => Promise<void>;
  onActualizarConciliacionForma?: (accion: {
    etapa: "supervisor" | "tesoreria";
    estado: boolean;
    claveForma: string;
    rendicionId: number;
    nota?: string;
    idcontrolcaja?: number | null;
    idformaPago?: number | null;
  }) => Promise<void>;
  monchisTesoreriaMontos?: MonchisTesoreriaMonto[];
}

interface GrupoConciliacion {
  key: string;
  sucursal: string;
  turno: string;
  caja: string;
  cajero: string;
  estadoTurno: string | null;
  estadoCaja: string | null;
  apertura: string | null;
  cierre: string | null;
  formas: FilaPorFormaPago[];
  totalSistema: number;
}

export interface GrupoContexto {
  key: string;
  fuente?: string | null;
  sucursal: string;
  turno: string;
  caja: string;
  cajero: string;
  idcontrolcaja: number | null;
  idsucursal: number | null;
  idturno: number | null;
  idcajero: number | null;
  descEstadoTurno: string | null;
  descEstadoCaja: string | null;
  fechaHoraAperturaTurno: string | null;
  fechaApertura: string | null;
  fechaCierre: string | null;
  formasExistentes: string[];
  montosSistemaPorForma: Record<
    string,
    {
      etiqueta: string;
      monto: number;
    }
  >;
}

export interface MonchisTesoreriaMonto {
  idcontrolcaja: number;
  idformaPago: number | null;
  formaPago: string | null;
  monto: number;
  totalRegistros?: number;
}

type PlataformaIntegracion = "monchis";

const PLATAFORMAS_DISPONIBLES: { id: PlataformaIntegracion; label: string }[] = [
  { id: "monchis", label: "Monchis" },
];

type PlataformaConciliacionRegistro = {
  id: number;
  registroId: string;
  movimientoId: string | null;
  fechaDia: string | null;
  monto: number | null;
  estado: string | null;
  empresaCodigo: string | null;
  empresaNombre: string | null;
  observacion: string | null;
  usuarioId: number | null;
  usuarioNombre: string | null;
  creadoEn: string | null;
  actualizadoEn: string | null;
  sucursalRegistro: string | null;
  sucursalPlataforma: string | null;
};

type PlataformaConciliacionResponse = {
  plataforma: string;
  total: number;
  totalMonto: number;
  registros: PlataformaConciliacionRegistro[];
};

const formatMonto = (valor: number) =>
  valor.toLocaleString("es-PY", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });

const formatMontoLabel = (valor: number) => `Gs ${formatMonto(valor)}`;

const formatFecha = (valor: string | null) => {
  if (!valor) return "—";
  try {
    const fecha = new Date(valor);
    return fecha.toLocaleString("es-PY");
  } catch {
    return valor;
  }
};

const formatFechaCorta = (valor: string | null) => {
  if (!valor) return "—";
  try {
    const fecha = new Date(valor);
    return fecha.toLocaleDateString("es-PY");
  } catch {
    return valor;
  }
};

const abreviarIdentificador = (valor: string | null | undefined, longitud: number = 18) => {
  if (!valor) {
    return "—";
  }
  if (valor.length <= longitud) {
    return valor;
  }
  const mitad = Math.floor((longitud - 1) / 2);
  return `${valor.slice(0, mitad)}…${valor.slice(valor.length - mitad)}`;
};

const normalizarClaveForma = (valor?: string | null) => {
  if (!valor) return "";
  const texto = valor
    .toString()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toUpperCase();
  const sinPuntuacion = texto.replace(/[^A-Z0-9]/g, "");
  return sinPuntuacion.replace(/^[0-9]+/, "");
};

const construirMapaFormasCajero = (formas?: RendicionEstadoCaja["formasPago"]) => {
  const porId = new Map<number, number>();
  const porDescripcion = new Map<string, number>();
  (formas ?? []).forEach((item) => {
    const monto = Number(item.monto ?? 0);
    if (!Number.isFinite(monto)) {
      return;
    }
    if (item.idFormaPago != null && Number.isFinite(Number(item.idFormaPago))) {
      const id = Number(item.idFormaPago);
      porId.set(id, (porId.get(id) ?? 0) + monto);
    }
    const claveDescripcion = normalizarClaveForma(item.descripcion);
    if (claveDescripcion) {
      porDescripcion.set(claveDescripcion, (porDescripcion.get(claveDescripcion) ?? 0) + monto);
    }
  });
  return { porId, porDescripcion };
};

const obtenerMontoCajero = (
  fila: FilaPorFormaPago,
  mapa: { porId: Map<number, number>; porDescripcion: Map<string, number> } | null,
) => {
  if (!mapa) {
    return null;
  }
  if (mapa.porId.size === 0 && mapa.porDescripcion.size === 0) {
    return null;
  }
  if (fila.idformaPago != null) {
    const id = Number(fila.idformaPago);
    if (Number.isFinite(id) && mapa.porId.has(id)) {
      return mapa.porId.get(id) ?? null;
    }
  }
  const claveDescripcion = normalizarClaveForma(fila.formaPago);
  if (claveDescripcion && mapa.porDescripcion.has(claveDescripcion)) {
    return mapa.porDescripcion.get(claveDescripcion) ?? null;
  }
  return null;
};

const esFormaPagoMonchis = (fila: FilaPorFormaPago) => {
  if (fila.idformaPago === 4) {
    return true;
  }
  const descripcion = normalizarClaveForma(fila.formaPago ?? "");
  return descripcion.startsWith("04MONCHISPAGOONLINE");
};

const esFormaPagoCredito = (fila: FilaPorFormaPago) =>
  String(fila.formaPago ?? "").trim().toUpperCase() === "CREDITO";

const describirEstadoConciliacion = (valor?: string | null) => {
  const estado = (valor ?? "pendiente").toString().toLowerCase();
  if (estado === "aprobada") {
    return "APROBADA";
  }
  if (estado === "rechazada") {
    return "RECHAZADA";
  }
  return "PENDIENTE";
};

const estiloBadgeActivo = (color: string, activo: boolean) =>
  activo
    ? {
        border: `2px solid var(--bs-${color})`,
      }
    : undefined;

const ControlCajaPanel = ({
  filasPorFormaPago,
  filasReporte,
  cargando,
  error,
  onReload,
  onAgregarFormaPago,
  onVerFacturas,
  onEliminarFormaManual,
  rendicionesPorCaja = {},
  puedeConciliarSupervisor,
  puedeConciliarTesoreria,
  onActualizarConciliacion,
  onActualizarConciliacionForma,
  monchisTesoreriaMontos = [],
}: ControlCajaPanelProps) => {
  const headerCellStyle = { border: "1px solid rgba(var(--bs-body-color-rgb), 0.3)" };
  const [valoresTesoreria, setValoresTesoreria] = useState<Record<string, string>>({});
  const [valoresSupervisor, setValoresSupervisor] = useState<Record<string, string>>({});
  const [fuenteDiferencia, setFuenteDiferencia] = useState<"tesoreria" | "supervisor" | "cajero">("tesoreria");
  const registrosConciliadosTesoreriaRef = useRef<Record<string, number>>({});
  const [accionando, setAccionando] = useState<string | null>(null);
  const [observacionesSupervisor, setObservacionesSupervisor] = useState<Record<string, string>>({});
  const [observacionesTesoreria, setObservacionesTesoreria] = useState<Record<string, string>>({});
  const [modalDeposito, setModalDeposito] = useState<{
    caja: CajaDiaAnterior | null;
    sucursal: SucursalRendicion | null;
  }>({ caja: null, sucursal: null });
  const [modalPlataformas, setModalPlataformas] = useState<{
    visible: boolean;
    contexto: GrupoContexto | null;
  }>({ visible: false, contexto: null });
  const [plataformasDatos, setPlataformasDatos] = useState<PlataformaConciliacionResponse | null>(null);
  const [plataformasCargando, setPlataformasCargando] = useState(false);
  const [plataformasError, setPlataformasError] = useState<string | null>(null);
  const [plataformaSeleccionada, setPlataformaSeleccionada] = useState<PlataformaIntegracion>("monchis");
  const abrirDepositoParaCaja = useCallback(
    (contexto: GrupoContexto) => {
      if (!contexto.idcontrolcaja) {
        return;
      }
      const rendicion = rendicionesPorCaja?.[contexto.idcontrolcaja] ?? null;
      const formasPreparadas =
        rendicion?.formasPago?.map((forma) => ({
          descripcion: forma.descripcion ?? null,
          monto: typeof forma.monto === "number" ? forma.monto : Number(forma.monto ?? 0),
        })) ?? [];
      const cajaPreparada: CajaDiaAnterior = {
        idcontrolcaja: contexto.idcontrolcaja,
        idturno: contexto.idturno ?? null,
        idsucursal: contexto.idsucursal ?? null,
        sucursal: contexto.sucursal ?? null,
        sucursalNombre: contexto.sucursal ?? null,
        turno: contexto.turno ?? null,
        cajero: contexto.cajero ?? null,
        idcajero: contexto.idcajero ?? null,
        fechaApertura: contexto.fechaApertura ?? null,
        fechaCierre: contexto.fechaCierre ?? null,
        descEstadoCaja: contexto.descEstadoCaja ?? null,
        descEstadoTurno: contexto.descEstadoTurno ?? null,
        estado: null,
        totalFondoDeclarado: rendicion?.totalFondo ?? null,
        rendicionUsuario: rendicion?.usuarioNombre ?? null,
        rendicionRegistradaEn: rendicion?.fechaRendicion ?? rendicion?.actualizadoEn ?? null,
        cajeroRegistrado: rendicion?.cajeroNombre ?? contexto.cajero ?? null,
        efectivoDeclarado: rendicion?.totalFormasPago ?? rendicion?.totalFondo ?? null,
        formasPago: formasPreparadas,
      };
      const sucursalItem: SucursalRendicion = {
        idsucursal: contexto.idsucursal ?? 0,
        sucursalNombre: contexto.sucursal ?? null,
        fuente: null,
        empresa: null,
      };
      setModalDeposito({ caja: cajaPreparada, sucursal: sucursalItem });
    },
    [rendicionesPorCaja],
  );

  const cargarConciliacionesPlataforma = useCallback(
    async (contexto: GrupoContexto, plataforma: PlataformaIntegracion) => {
      if (!contexto.idcontrolcaja) {
        setPlataformasError("No encontramos la caja seleccionada.");
        setPlataformasDatos(null);
        return;
      }
      setPlataformasCargando(true);
      setPlataformasError(null);
      try {
        const params = new URLSearchParams({
          caja: String(contexto.idcontrolcaja),
          plataforma,
        });
        const respuesta = await requestJson<PlataformaConciliacionResponse>(
          `/api/tesoreria/monchis/conciliaciones/caja?${params.toString()}`,
        );
        setPlataformasDatos(respuesta);
      } catch (error) {
        setPlataformasDatos(null);
        setPlataformasError(
          error instanceof Error
            ? error.message
            : "No pudimos cargar las conciliaciones para esta plataforma.",
        );
      } finally {
        setPlataformasCargando(false);
      }
    },
    [],
  );

  const abrirModalPlataformas = (contexto: GrupoContexto) => {
    if (!contexto.idcontrolcaja) {
      return;
    }
    setPlataformaSeleccionada("monchis");
    setPlataformasDatos(null);
    setPlataformasError(null);
    setModalPlataformas({ visible: true, contexto });
    void cargarConciliacionesPlataforma(contexto, "monchis");
  };

  const cerrarModalPlataformas = () => {
    setModalPlataformas({ visible: false, contexto: null });
    setPlataformasDatos(null);
    setPlataformasError(null);
    setPlataformasCargando(false);
  };

  const manejarCambioPlataformaModal = (valor: PlataformaIntegracion) => {
    setPlataformaSeleccionada(valor);
    if (modalPlataformas.contexto) {
      void cargarConciliacionesPlataforma(modalPlataformas.contexto, valor);
    }
  };

  const montosTesoreriaMonchisMap = useMemo(() => {
    const mapa = new Map<
      number,
      {
        porId: Map<number, { monto: number; totalRegistros: number }>;
        porDescripcion: Map<string, { monto: number; totalRegistros: number }>;
      }
    >();
    (monchisTesoreriaMontos ?? []).forEach((registro) => {
      const controlId = Number(registro.idcontrolcaja);
      if (!Number.isFinite(controlId) || controlId <= 0) {
        return;
      }
      if (!mapa.has(controlId)) {
        mapa.set(controlId, { porId: new Map(), porDescripcion: new Map() });
      }
      const bucket = mapa.get(controlId)!;
      const monto = Number(registro.monto ?? 0);
      const total = Number(registro.totalRegistros ?? 0);
      if (registro.idformaPago != null && Number.isFinite(Number(registro.idformaPago))) {
        const idForma = Number(registro.idformaPago);
        const previo = bucket.porId.get(idForma) ?? { monto: 0, totalRegistros: 0 };
        bucket.porId.set(idForma, {
          monto: previo.monto + monto,
          totalRegistros: previo.totalRegistros + total,
        });
      }
      const claveDescripcion = normalizarClaveForma(registro.formaPago ?? "");
      if (claveDescripcion) {
        const previo = bucket.porDescripcion.get(claveDescripcion) ?? { monto: 0, totalRegistros: 0 };
        bucket.porDescripcion.set(claveDescripcion, {
          monto: previo.monto + monto,
          totalRegistros: previo.totalRegistros + total,
        });
      }
    });
    return mapa;
  }, [monchisTesoreriaMontos]);

  const fuentesDisponibles = useMemo(
    () => {
      const lista: { clave: "tesoreria" | "supervisor" | "cajero"; titulo: string }[] = [];
      if (puedeConciliarTesoreria) {
        lista.push({ clave: "tesoreria", titulo: "Dif. Tesorería" });
      }
      if (puedeConciliarSupervisor) {
        lista.push({ clave: "supervisor", titulo: "Dif. Supervisor" });
      }
      lista.push({ clave: "cajero", titulo: "Dif. Cajero" });
      return lista;
    },
    [puedeConciliarSupervisor, puedeConciliarTesoreria],
  );

  useEffect(() => {
    const permitidas = fuentesDisponibles.map((item) => item.clave);
    if (!permitidas.includes(fuenteDiferencia)) {
      setFuenteDiferencia(permitidas[0] ?? "cajero");
    }
  }, [fuenteDiferencia, fuentesDisponibles]);

  useEffect(() => {
    setObservacionesSupervisor({});
    setObservacionesTesoreria({});
    registrosConciliadosTesoreriaRef.current = {};
  }, [rendicionesPorCaja, filasPorFormaPago]);

  useEffect(() => {
    if (montosTesoreriaMonchisMap.size === 0) {
      return;
    }
    setValoresTesoreria((prev) => {
      let huboCambio = false;
      let actualizado = prev;
      filasPorFormaPago.forEach((fila) => {
        if (!fila.idcontrolcaja) {
          return;
        }
        const registro = montosTesoreriaMonchisMap.get(fila.idcontrolcaja);
        if (!registro) {
          return;
        }
        const valorActual = actualizado[fila.id];
        if (typeof valorActual === "string" && valorActual.trim().length > 0) {
          return;
        }
        let monto: number | undefined;
        let registrosConciliados: number | undefined;
        if (fila.idformaPago != null && registro.porId.has(fila.idformaPago)) {
          const datos = registro.porId.get(fila.idformaPago);
          monto = datos?.monto;
          registrosConciliados = datos?.totalRegistros;
        } else {
          const claveDescripcion = normalizarClaveForma(fila.formaPago);
          if (claveDescripcion && registro.porDescripcion.has(claveDescripcion)) {
            const datos = registro.porDescripcion.get(claveDescripcion);
            monto = datos?.monto;
            registrosConciliados = datos?.totalRegistros;
          }
        }
        if (monto !== undefined && monto !== null) {
          if (actualizado === prev) {
            actualizado = { ...prev };
          }
          actualizado[fila.id] = String(monto);
          huboCambio = true;
        }
        if (registrosConciliados !== undefined) {
          registrosConciliadosTesoreriaRef.current[fila.id] = registrosConciliados;
        }
      });
      return huboCambio ? actualizado : prev;
    });
  }, [filasPorFormaPago, montosTesoreriaMonchisMap]);

  const obtenerValorTesoreria = (fila: FilaPorFormaPago) => {
    const texto = valoresTesoreria[fila.id] ?? "";
    const valor = parseFloat(texto);
    return Number.isFinite(valor) ? valor : 0;
  };

  const obtenerValorSupervisor = (fila: FilaPorFormaPago) => {
    const texto = valoresSupervisor[fila.id] ?? "";
    const valor = parseFloat(texto);
    return Number.isFinite(valor) ? valor : 0;
  };
  const grupos = useMemo<GrupoConciliacion[]>(() => {
    if (filasPorFormaPago.length === 0) {
      return [];
    }
    const mapa = new Map<string, GrupoConciliacion>();
    filasPorFormaPago.forEach((fila) => {
      const key = `${fila.sucursal}|${fila.turno}|${fila.caja}|${fila.cajero}`;
      const existente = mapa.get(key);
      if (existente) {
        existente.formas.push(fila);
        existente.totalSistema += fila.monto;
      } else {
        mapa.set(key, {
          key,
          sucursal: fila.sucursal,
          turno: fila.turno,
          caja: fila.caja,
          cajero: fila.cajero,
          estadoTurno: fila.descEstadoTurno ?? null,
          estadoCaja: fila.descEstadoCaja ?? null,
          apertura: fila.fechaHoraAperturaTurno ?? fila.fechaApertura ?? null,
          cierre: fila.fechaCierre ?? null,
          formas: [fila],
          totalSistema: fila.monto,
        });
      }
    });
    return Array.from(mapa.values()).sort((a, b) => a.sucursal.localeCompare(b.sucursal));
  }, [filasPorFormaPago]);

  const describirEstadoConciliacion = (valor?: string | null) => {
    const estado = (valor ?? "pendiente").toString().toLowerCase();
    if (estado === "aprobada") {
      return "APROBADA";
    }
    if (estado === "rechazada") {
      return "RECHAZADA";
    }
    return "PENDIENTE";
  };

  if (error) {
    return (
      <Alert color="danger">
        {error}{" "}
        <Button color="link" className="p-0 align-baseline" onClick={onReload}>
          Reintentar
        </Button>
      </Alert>
    );
  }

  if (cargando) {
    return (
      <div className="text-center py-5">
        <Spinner color="primary" />
        <p className="text-muted mt-2 mb-0">Buscando movimientos y comparaciones...</p>
      </div>
    );
  }

  if (grupos.length === 0) {
    return (
      <Card>
        <CardBody className="text-center">
          <CardTitle tag="h5" className="mb-2">
            No encontramos conciliaciones
          </CardTitle>
          <p className="text-muted mb-0">Ajustá los filtros o seleccioná otro período para ver los resultados.</p>
        </CardBody>
      </Card>
    );
  }

  return (
    <div className="d-flex flex-column gap-3">
      {grupos.map((grupo) => {
        const filasFacturas = filasReporte.filter((fila) => {
          const sucursal = fila.sucursal ?? (fila.idsucursal != null ? `Sucursal ${fila.idsucursal}` : "Sin sucursal");
          const turno = fila.turno ?? (fila.idturno != null ? `Turno ${fila.idturno}` : "Sin turno");
          const caja = fila.idcontrolcaja != null ? `Caja ${fila.idcontrolcaja}` : "Sin caja";
          const cajero = fila.cajero ?? "Sin cajero";
          return sucursal === grupo.sucursal && turno === grupo.turno && caja === grupo.caja && cajero === grupo.cajero;
        });
        const totalFacturas = filasFacturas.reduce((acc, fila) => acc + fila.monto, 0);
        const totalTesoreriaGrupo = grupo.formas.reduce((acc, forma) => acc + obtenerValorTesoreria(forma), 0);
        const totalSupervisorGrupo = grupo.formas.reduce((acc, forma) => acc + obtenerValorSupervisor(forma), 0);
        const idcontrolcajaGrupo = (() => {
          const candidata = grupo.formas.find((forma) => Number.isFinite(forma.idcontrolcaja ?? NaN));
          if (!candidata || candidata.idcontrolcaja == null) {
            return null;
          }
          const valor = Number(candidata.idcontrolcaja);
          return Number.isFinite(valor) ? valor : null;
        })();
        const rendicionGrupo = idcontrolcajaGrupo != null ? rendicionesPorCaja[idcontrolcajaGrupo] : undefined;
        const formasCajero = rendicionGrupo?.formasPago ?? [];
        const totalCajeroGrupo = formasCajero.reduce((acc, item) => {
          const monto = Number(item.monto ?? 0);
          return acc + (Number.isFinite(monto) ? monto : 0);
        }, 0);
        const mapaCajero = construirMapaFormasCajero(formasCajero);
        const obtenerTotalPorFuente = (clave: "tesoreria" | "supervisor" | "cajero") => {
          if (clave === "supervisor") {
            return totalSupervisorGrupo;
          }
          if (clave === "cajero") {
            return totalCajeroGrupo;
          }
          return totalTesoreriaGrupo;
        };
        const obtenerTituloFuente = (clave: "tesoreria" | "supervisor" | "cajero") => {
          if (clave === "supervisor") {
            return "Supervisor";
          }
          if (clave === "cajero") {
            return "Cajero";
          }
          return "Tesorería";
        };
        const obtenerColorFuente = (clave: "tesoreria" | "supervisor" | "cajero", diferenciaValor: number) => {
          const esCero = Math.abs(diferenciaValor) < 1;
          if (clave === "tesoreria") {
            return esCero ? "success" : "warning";
          }
          if (clave === "supervisor") {
            return esCero ? "success" : "info";
          }
          return esCero ? "success" : "secondary";
        };
        const totalFuenteGrupo = obtenerTotalPorFuente(fuenteDiferencia);
        const diferencia = totalFuenteGrupo - totalFacturas;
        const estadoDiferencia =
          Math.abs(diferencia) < 1 ? "success" : diferencia > 0 ? "warning" : "danger";
        const baseForma = grupo.formas[0] ?? null;
        const formasExistentes = grupo.formas
          .filter(
            (forma) =>
              !forma.id.startsWith("CONTROL_ROW:") &&
              typeof forma.formaPago === "string" &&
              forma.formaPago.trim().length > 0,
          )
          .map((forma) => forma.formaPago ?? "Sin descripción");
        const montosSistemaPorForma = grupo.formas.reduce<
          Record<
            string,
            {
              etiqueta: string;
              monto: number;
            }
          >
        >((acc, forma) => {
          const etiqueta = forma.formaPago || "Sin descripción";
          const clave = normalizarClaveForma(etiqueta);
          if (!acc[clave]) {
            acc[clave] = { etiqueta, monto: 0 };
          }
          acc[clave].monto += forma.monto;
          return acc;
        }, {});
        const contexto: GrupoContexto = {
          key: grupo.key,
          fuente: baseForma?.fuente ?? null,
          sucursal: grupo.sucursal,
          turno: grupo.turno,
          caja: grupo.caja,
          cajero: grupo.cajero,
          idcontrolcaja: idcontrolcajaGrupo ?? null,
          idsucursal: baseForma?.idsucursal ?? null,
          idturno: baseForma?.idturno ?? null,
          idcajero: baseForma?.idcajero ?? null,
          descEstadoTurno: grupo.estadoTurno ?? null,
          descEstadoCaja: grupo.estadoCaja ?? null,
          fechaHoraAperturaTurno: baseForma?.fechaHoraAperturaTurno ?? grupo.apertura ?? null,
          fechaApertura: baseForma?.fechaApertura ?? grupo.apertura ?? null,
          fechaCierre: grupo.cierre ?? baseForma?.fechaCierre ?? null,
          formasExistentes,
          montosSistemaPorForma,
        };
        const totalAnuladas = filasFacturas.filter(
          (fila) => String(fila.tipoFactura ?? "").toUpperCase() === "ANULADA",
        ).length;
        const totalCredito = filasFacturas.filter((fila) =>
          String(fila.formaPago ?? "").toUpperCase().includes("CREDITO"),
        ).length;

        const supervisorEstadoTexto = describirEstadoConciliacion(rendicionGrupo?.supervisorEstado);
        const tesoreriaEstadoTexto = describirEstadoConciliacion(rendicionGrupo?.tesoreriaEstado);
        const supervisorAprobado = (rendicionGrupo?.supervisorEstado ?? "pendiente").toLowerCase() === "aprobada";
        const tesoreriaAprobado = (rendicionGrupo?.tesoreriaEstado ?? "pendiente").toLowerCase() === "aprobada";
        const supervisorConciliaciones = rendicionGrupo?.supervisorConciliaciones ?? 0;
        const tesoreriaConciliaciones = rendicionGrupo?.tesoreriaConciliaciones ?? 0;
        const fechaConfirmacion = rendicionGrupo?.confirmadoEn ?? null;
        const idTurnoGrupo = grupo.formas[0]?.idturno ?? null;
        const totalFormasSupervisorConciliables = grupo.formas.filter((forma) => !esFormaPagoCredito(forma)).length;
        const totalSupervisorConciliado = grupo.formas.filter(
          (forma) => !esFormaPagoCredito(forma) && rendicionGrupo?.formasConciliadas?.[forma.id]?.supervisor?.estado,
        ).length;
        const totalFormasTesoreriaConciliables = grupo.formas.filter((forma) => !esFormaPagoCredito(forma)).length;
        const totalTesoreriaConciliado = grupo.formas.filter(
          (forma) => !esFormaPagoCredito(forma) && rendicionGrupo?.formasConciliadas?.[forma.id]?.tesoreria?.estado,
        ).length;
        const todasSupervisorListas =
          totalFormasSupervisorConciliables === 0 ||
          (totalSupervisorConciliado === totalFormasSupervisorConciliables && totalFormasSupervisorConciliables > 0);
        const todasTesoreriaListas =
          totalFormasTesoreriaConciliables === 0 ||
          (totalTesoreriaConciliado === totalFormasTesoreriaConciliables && totalFormasTesoreriaConciliables > 0);
        const puedeOperarSupervisor = puedeConciliarSupervisor && Boolean(rendicionGrupo?.rendicionId);
        const puedeOperarTesoreria = puedeConciliarTesoreria && Boolean(rendicionGrupo?.rendicionId);
        const tieneSubEncabezado = puedeConciliarTesoreria || puedeConciliarSupervisor;

        const manejarAccionConciliacion = async (
          etapa: "supervisor" | "tesoreria",
          estado: "aprobada" | "rechazada" | "pendiente",
        ) => {
          if (!onActualizarConciliacion || !rendicionGrupo?.rendicionId) {
            return;
          }
          let nota: string | undefined;
          if (estado === "rechazada") {
            nota = window.prompt("Motivo del rechazo (opcional)", "") ?? undefined;
          }
          const claveAccion = `${etapa}-${rendicionGrupo.rendicionId}`;
          setAccionando(claveAccion);
          try {
            await onActualizarConciliacion({
              etapa,
              estado,
              nota,
              rendicionId: rendicionGrupo.rendicionId,
            });
          } catch (error) {
            const mensaje = error instanceof Error ? error.message : "No se pudo actualizar la conciliación.";
            window.alert(mensaje);
          } finally {
            setAccionando(null);
          }
        };

        const manejarConciliacionFormaFila = async (
          forma: FilaPorFormaPago,
          etapa: "supervisor" | "tesoreria",
          estadoActual: boolean,
          claveAccion: string,
        ) => {
          if (!onActualizarConciliacionForma || !rendicionGrupo?.rendicionId) {
            return;
          }
          const nuevoEstado = !estadoActual;
          const notaSupervisor =
            etapa === "supervisor"
              ? (Object.prototype.hasOwnProperty.call(observacionesSupervisor, forma.id)
                  ? observacionesSupervisor[forma.id]
                  : rendicionGrupo?.formasConciliadas?.[forma.id]?.supervisor?.nota ?? "")
              : undefined;
          const notaTesoreria =
            etapa === "tesoreria"
              ? (Object.prototype.hasOwnProperty.call(observacionesTesoreria, forma.id)
                  ? observacionesTesoreria[forma.id]
                  : rendicionGrupo?.formasConciliadas?.[forma.id]?.tesoreria?.nota ?? "")
              : undefined;
          const payload = {
            rendicionId: rendicionGrupo.rendicionId,
            etapa,
            claveForma: forma.id,
            estado: nuevoEstado,
            idcontrolcaja: forma.idcontrolcaja ?? null,
            idformaPago: forma.idformaPago ?? null,
            nota: etapa === "supervisor" ? notaSupervisor : notaTesoreria,
          };
          setAccionando(claveAccion);
          try {
            await onActualizarConciliacionForma(payload);
          } catch (error) {
            const mensaje = error instanceof Error ? error.message : "No se pudo actualizar la conciliación.";
            window.alert(mensaje);
          } finally {
            setAccionando(null);
          }
        };

        const auditoriaCards = [
          {
            key: "sistema",
            titulo: "Sistema (JEDI)",
            descripcion: "",
            icono: "ri-terminal-window-line",
            color: "primary",
            contenido: (
              <div className="text-muted small lh-sm">
                <div>
                  <span className="fw-semibold">Estado turno: </span>
                  {grupo.estadoTurno ?? "Sin dato"}
                </div>
                <div>
                  <span className="fw-semibold">Estado caja: </span>
                  {grupo.estadoCaja ?? "Sin dato"}
                </div>
                <div>
                  <span className="fw-semibold">Apertura: </span>
                  {formatFecha(grupo.apertura)}
                </div>
                <div>
                  <span className="fw-semibold">Cierre: </span>
                  {formatFecha(grupo.cierre)}
                </div>
              </div>
            ),
          },
          {
            key: "cajero",
            titulo: "Declaración del cajero",
            descripcion: "",
            icono: "ri-user-3-line",
            color: "success",
            contenido: rendicionGrupo ? (
              <div className="text-muted small lh-sm">
                <div>
                  <span className="fw-semibold">Estado: </span>
                  {rendicionGrupo.estado ?? "BORRADOR"}
                </div>
                <div>
                  <span className="fw-semibold">Confirmado por: </span>
                  {rendicionGrupo.confirmadoPor ? (
                    <>
                      {rendicionGrupo.confirmadoPor}
                      {fechaConfirmacion ? (
                        <span className="text-muted"> · {formatFecha(fechaConfirmacion)}</span>
                      ) : null}
                    </>
                  ) : (
                    "Pendiente de confirmación."
                  )}
                </div>
                <div>
                  <span className="fw-semibold">ID rendición: </span>
                  {rendicionGrupo.rendicionId ?? "—"}
                </div>
                <div>
                  <span className="fw-semibold">ID control caja: </span>
                  {idcontrolcajaGrupo ?? "—"}
                </div>
                <div>
                  <span className="fw-semibold">ID turno: </span>
                  {idTurnoGrupo ?? "—"}
                </div>
              </div>
            ) : (
              <p className="text-muted small fst-italic mb-0">No hay rendición del cajero para esta caja.</p>
            ),
          },
          {
            key: "supervisor",
            titulo: "Supervisor",
            descripcion: "",
            icono: "ri-shield-user-line",
            color: "info",
            contenido: (
              <div className="text-muted small lh-sm">
                <div>
                  <span className="fw-semibold">Estado: </span>
                  {supervisorEstadoTexto}
                </div>
                {rendicionGrupo?.supervisorUsuarioNombre ? (
                  <div>
                    <span className="fw-semibold">Por: </span>
                    {rendicionGrupo.supervisorUsuarioNombre}
                  </div>
                ) : null}
                {rendicionGrupo?.supervisorActualizadoEn ? (
                  <div>
                    <span className="fw-semibold">Actualizado: </span>
                    {formatFecha(rendicionGrupo.supervisorActualizadoEn ?? null)}
                  </div>
                ) : null}
                {rendicionGrupo?.supervisorNota ? (
                  <div>
                    <span className="fw-semibold">Nota: </span>
                    {rendicionGrupo.supervisorNota}
                  </div>
                ) : null}
                <div>
                  <span className="fw-semibold">Conciliaciones: </span>
                  {supervisorConciliaciones}
                </div>
              </div>
            ),
          },
          {
            key: "tesoreria",
            titulo: "Tesorería",
            descripcion: "",
            icono: "ri-safe-line",
            color: "warning",
            contenido: (
              <div className="text-muted small lh-sm">
                <div>
                  <span className="fw-semibold">Estado: </span>
                  {tesoreriaEstadoTexto}
                </div>
                {rendicionGrupo?.tesoreriaUsuarioNombre ? (
                  <div>
                    <span className="fw-semibold">Por: </span>
                    {rendicionGrupo.tesoreriaUsuarioNombre}
                  </div>
                ) : null}
                {rendicionGrupo?.tesoreriaActualizadoEn ? (
                  <div>
                    <span className="fw-semibold">Actualizado: </span>
                    {formatFecha(rendicionGrupo.tesoreriaActualizadoEn ?? null)}
                  </div>
                ) : null}
                {rendicionGrupo?.tesoreriaNota ? (
                  <div>
                    <span className="fw-semibold">Nota: </span>
                    {rendicionGrupo.tesoreriaNota}
                  </div>
                ) : null}
                <div>
                  <span className="fw-semibold">Conciliaciones: </span>
                  {tesoreriaConciliaciones}
                </div>
              </div>
            ),
          },
        ];
        const auditoriaCardsVisibles = auditoriaCards.filter((card) => {
          if (card.key === "tesoreria") {
            return puedeConciliarTesoreria;
          }
          if (card.key === "supervisor") {
            return puedeConciliarSupervisor;
          }
          return true;
        });
        const resumenTotales: string[] = [];
        if (puedeConciliarTesoreria) {
          resumenTotales.push(`Tesorería: ${formatMonto(totalTesoreriaGrupo)} Gs.`);
        }
        if (puedeConciliarSupervisor) {
          resumenTotales.push(`Supervisor: ${formatMonto(totalSupervisorGrupo)} Gs.`);
        }
        resumenTotales.push(
          `Cajero: ${formasCajero.length > 0 ? `${formatMonto(totalCajeroGrupo)} Gs.` : "Sin dato"}`,
        );
        resumenTotales.push(`Sistema: ${formatMonto(totalFacturas)} Gs.`);
        const textoResumenTotales = resumenTotales.join(" · ");

        return (
          <Card
            key={grupo.key}
            className="border-0 shadow-sm mb-4"
            style={{ borderTop: `4px solid var(--bs-${estadoDiferencia})` }}
          >
            <CardBody className="p-4">
              <div className="d-flex flex-wrap justify-content-between gap-3 mb-3">
                <div>
                  <CardTitle tag="h5" className="mb-1">
                    {grupo.sucursal} · {grupo.turno} · {grupo.caja}
                  </CardTitle>
                  <p className="text-muted mb-0">{grupo.cajero || "Cajero no informado"}</p>
                  <div className="d-flex flex-wrap gap-2 mt-2">
                    <Badge color={grupo.estadoTurno === "CERRADO" ? "soft-success" : "soft-warning"}>
                      Turno: {grupo.estadoTurno ?? "Sin dato"}
                    </Badge>
                    <Badge color={grupo.estadoCaja === "CERRADO" ? "soft-success" : "soft-warning"}>
                      Caja: {grupo.estadoCaja ?? "Sin dato"}
                    </Badge>
                    {grupo.apertura && (
                      <Badge color="soft-secondary">Apertura: {formatFecha(grupo.apertura)}</Badge>
                    )}
                    {grupo.cierre && <Badge color="soft-secondary">Cierre: {formatFecha(grupo.cierre)}</Badge>}
                  </div>
                </div>
                <div className="text-end">
                  <div className="d-flex gap-3 justify-content-end mb-2 flex-wrap">
                    {fuentesDisponibles.map((fuente) => {
                      const total = obtenerTotalPorFuente(fuente.clave);
                      const diff = total - totalFacturas;
                      const color = obtenerColorFuente(fuente.clave, diff);
                      return (
                        <button
                          type="button"
                          key={fuente.clave}
                          className="border-0 bg-transparent text-start p-0"
                          onClick={() => setFuenteDiferencia(fuente.clave)}
                        >
                          <span className="text-muted text-uppercase fs-12 d-block">{fuente.titulo}</span>
                          <Badge
                            color={color}
                            className="fs-6 d-inline-flex justify-content-center px-3"
                            style={{
                              minWidth: 120,
                              ...estiloBadgeActivo(color, fuenteDiferencia === fuente.clave),
                            }}
                          >
                            {formatMonto(diff)} Gs.
                          </Badge>
                        </button>
                      );
                    })}
                  </div>
                  <div className="text-muted small">{textoResumenTotales}</div>
                </div>
              </div>

              <div className="card crm-widget mb-4">
                <div className="card-body p-0">
                  <div className="row row-cols-xxl-4 row-cols-md-2 row-cols-1 g-0">
                    {auditoriaCardsVisibles.map((card) => (
                      <div className="col" key={card.key}>
                        <div className="py-4 px-3 h-100 d-flex flex-column gap-2">
                          <p className="text-muted text-uppercase fs-13 mb-0">
                            {card.titulo}
                            <i className={`${card.icono} text-${card.color} fs-18 float-end align-middle`} />
                          </p>
                          <div className="text-muted small">{card.contenido}</div>
                          {card.key === "supervisor" && puedeOperarSupervisor && rendicionGrupo?.rendicionId ? (
                            <div className="mt-auto d-flex flex-wrap gap-2">
                              <Button
                                size="sm"
                                color="success"
                                disabled={
                                  supervisorAprobado ||
                                  accionando === `supervisor-${rendicionGrupo.rendicionId}` ||
                                  !todasSupervisorListas
                                }
                                onClick={() => {
                                  void manejarAccionConciliacion("supervisor", "aprobada");
                                }}
                              >
                                Conciliar
                              </Button>
                              {supervisorAprobado ? (
                                <Button
                                  size="sm"
                                  color="warning"
                                  disabled={accionando === `supervisor-${rendicionGrupo.rendicionId}`}
                                  onClick={() => {
                                    void manejarAccionConciliacion("supervisor", "pendiente");
                                  }}
                                >
                                  Reconciliar
                                </Button>
                              ) : null}
                            </div>
                          ) : null}
                          {card.key === "tesoreria" && puedeOperarTesoreria && rendicionGrupo?.rendicionId ? (
                            <div className="mt-auto d-flex flex-wrap gap-2">
                              <Button
                                size="sm"
                                color="success"
                                disabled={
                                  tesoreriaAprobado ||
                                  accionando === `tesoreria-${rendicionGrupo.rendicionId}` ||
                                  !todasTesoreriaListas
                                }
                                onClick={() => {
                                  void manejarAccionConciliacion("tesoreria", "aprobada");
                                }}
                              >
                                Conciliar
                              </Button>
                              {tesoreriaAprobado ? (
                                <Button
                                  size="sm"
                                  color="warning"
                                  disabled={accionando === `tesoreria-${rendicionGrupo.rendicionId}`}
                                  onClick={() => {
                                    void manejarAccionConciliacion("tesoreria", "pendiente");
                                  }}
                                >
                                  Reconciliar
                                </Button>
                              ) : null}
                            </div>
                          ) : null}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="table-responsive">
                <Table className="table-striped table-nowrap align-middle mb-0">
                  <thead
                    className="table-light border"
                    style={{ borderColor: "var(--bs-border-color)", borderWidth: "1px 1px 0 1px" }}
                  >
                    <tr>
                      <th
                        rowSpan={tieneSubEncabezado ? 2 : 1}
                        className="text-center align-middle"
                        style={headerCellStyle}
                      >
                        Forma de pago
                      </th>
                      {puedeConciliarTesoreria ? (
                        <th colSpan={3} className="text-center align-middle" style={headerCellStyle}>
                          Tesorería
                        </th>
                      ) : null}
                      {puedeConciliarSupervisor ? (
                        <th colSpan={3} className="text-center align-middle" style={headerCellStyle}>
                          Supervisor
                        </th>
                      ) : null}
                      <th
                        rowSpan={tieneSubEncabezado ? 2 : 1}
                        className="text-center align-middle"
                        style={headerCellStyle}
                      >
                        Cajero
                      </th>
                      <th
                        rowSpan={tieneSubEncabezado ? 2 : 1}
                        className="text-center align-middle"
                        style={headerCellStyle}
                      >
                        Sistema
                      </th>
                      <th
                        rowSpan={tieneSubEncabezado ? 2 : 1}
                        className="text-center align-middle"
                        style={{ ...headerCellStyle, width: 265 }}
                      >
                        <Input
                          type="select"
                          bsSize="sm"
                          disabled={fuentesDisponibles.length <= 1}
                          value={fuenteDiferencia}
                          onChange={(event) =>
                            setFuenteDiferencia(event.target.value as "tesoreria" | "supervisor" | "cajero")
                          }
                        >
                          {fuentesDisponibles.map((fuente) => (
                            <option key={fuente.clave} value={fuente.clave}>
                              {fuente.titulo}
                            </option>
                          ))}
                        </Input>
                      </th>
                    </tr>
                    {tieneSubEncabezado ? (
                      <tr>
                        {puedeConciliarTesoreria && (
                          <>
                            <th className="text-center" style={{ ...headerCellStyle, width: 140 }}>
                              Importe
                            </th>
                            <th className="text-center" style={{ ...headerCellStyle, width: 180 }}>
                              Observación
                            </th>
                            <th className="text-center" style={{ ...headerCellStyle, width: 120 }}>
                              Estado de
                              <br />
                              conciliación
                            </th>
                          </>
                        )}
                        {puedeConciliarSupervisor && (
                          <>
                            <th className="text-center" style={{ ...headerCellStyle, width: 140 }}>
                              Importe
                            </th>
                            <th className="text-center" style={{ ...headerCellStyle, width: 180 }}>
                              Observación
                            </th>
                            <th className="text-center" style={{ ...headerCellStyle, width: 120 }}>
                              Estado de
                              <br />
                              conciliación
                            </th>
                          </>
                        )}
                      </tr>
                    ) : null}
                  </thead>
                  <tbody>
                    {grupo.formas.map((forma) => {
                      const esCredito = String(forma.formaPago ?? "").toUpperCase() === "CREDITO";
                      const esMonchis = esFormaPagoMonchis(forma);
                      const valorTesoreriaTexto = valoresTesoreria[forma.id] ?? "";
                      const valorSupervisorTexto = valoresSupervisor[forma.id] ?? "";
                      const numericoTesoreria = obtenerValorTesoreria(forma);
                      const numericoSupervisor = obtenerValorSupervisor(forma);
                      const montoCajero = obtenerMontoCajero(forma, mapaCajero);
                      const base =
                        fuenteDiferencia === "supervisor"
                          ? numericoSupervisor
                          : fuenteDiferencia === "cajero"
                          ? montoCajero ?? 0
                          : numericoTesoreria;
                      const diferenciaFila = base - forma.monto;
                      const claveForma = forma.id;
                      const estadoTesoreriaForma =
                        rendicionGrupo?.formasConciliadas?.[claveForma]?.tesoreria?.estado ?? false;
                      const estadoSupervisorForma =
                        rendicionGrupo?.formasConciliadas?.[claveForma]?.supervisor?.estado ?? false;
                      const observacionSupervisorTexto = Object.prototype.hasOwnProperty.call(
                        observacionesSupervisor,
                        claveForma,
                      )
                        ? observacionesSupervisor[claveForma]
                        : rendicionGrupo?.formasConciliadas?.[claveForma]?.supervisor?.nota ?? "";
                      const observacionTesoreriaTexto = Object.prototype.hasOwnProperty.call(
                        observacionesTesoreria,
                        claveForma,
                      )
                        ? observacionesTesoreria[claveForma]
                        : rendicionGrupo?.formasConciliadas?.[claveForma]?.tesoreria?.nota ?? "";
                      const puedeMarcarTesoreria =
                        puedeConciliarTesoreria && Boolean(rendicionGrupo?.rendicionId) && !esCredito && !tesoreriaAprobado;
                      const puedeMarcarSupervisor =
                        puedeConciliarSupervisor && Boolean(rendicionGrupo?.rendicionId) && !esCredito && !supervisorAprobado;
                      const claveAccionTesoreria = `forma-${rendicionGrupo?.rendicionId}-tesoreria-${claveForma}`;
                      const claveAccionSupervisor = `forma-${rendicionGrupo?.rendicionId}-supervisor-${claveForma}`;
                      return (
                        <tr key={forma.id}>
                          <td className="align-middle">
                            <div className="d-flex align-items-center gap-2">
                              <span>{forma.formaPago || "Sin descripción"}</span>
                              {forma.esManual && onEliminarFormaManual && (
                                <Button
                                  color="link"
                                  size="sm"
                                  className="p-0 text-danger"
                                  onClick={() => onEliminarFormaManual(forma)}
                                >
                                  <i className="ri-delete-bin-line" />
                                </Button>
                              )}
                            </div>
                            {esMonchis ? (
                              <div className="text-muted small mt-1">
                                Monto proviene del módulo de conciliación de plataformas.
                              </div>
                            ) : null}
                          </td>
                          {puedeConciliarTesoreria ? (
                            <>
                              <td className="text-end align-middle" style={{ width: 140 }}>
                                {esCredito ? (
                                  <span className="text-muted">—</span>
                                ) : esMonchis ? (
                                  <div className="fw-semibold">{formatMontoLabel(numericoTesoreria)}</div>
                                ) : (
                                  <Input
                                    type="number"
                                    bsSize="sm"
                                    value={valorTesoreriaTexto}
                                    className="text-end"
                                    disabled={tesoreriaAprobado}
                                    onChange={(event) =>
                                      setValoresTesoreria((prev) => ({
                                        ...prev,
                                        [forma.id]: event.target.value,
                                      }))
                                    }
                                    onFocus={() => setFuenteDiferencia("tesoreria")}
                                  />
                                )}
                                {esMonchis ? (
                                  <div className="text-muted small mt-1">
                                    Monto proviene del módulo de conciliación de plataformas.
                                  </div>
                                ) : null}
                              </td>
                              <td className="align-middle" style={{ width: 180 }}>
                                {esCredito ? (
                                  <span className="text-muted">—</span>
                                ) : (
                                  <Input
                                    type="textarea"
                                    rows={1}
                                    bsSize="sm"
                                    placeholder="Observación Tesorería"
                                    className="form-control min-w-150"
                                    style={{ resize: "vertical" }}
                                    value={observacionTesoreriaTexto}
                                    disabled={!puedeConciliarTesoreria || tesoreriaAprobado}
                                    onChange={(event) =>
                                      setObservacionesTesoreria((prev) => ({
                                        ...prev,
                                        [claveForma]: event.target.value,
                                      }))
                                    }
                                  />
                                )}
                                {esMonchis ? (
                                  <div className="text-muted small mt-1">
                                    Registros conciliados: {registrosConciliadosTesoreriaRef.current[forma.id] ?? 0}
                                  </div>
                                ) : null}
                              </td>
                              <td className="text-center align-middle" style={{ width: 120 }}>
                            {esCredito ? (
                              <span className="text-muted">—</span>
                            ) : (
                              <div className="form-check form-switch form-switch-warning d-inline-flex align-items-center justify-content-center mb-0">
                                <Input
                                      type="checkbox"
                                      role="switch"
                                      className="form-check-input"
                                      id={`switch-tesoreria-${rendicionGrupo?.rendicionId ?? "na"}-${claveForma}`}
                                      checked={estadoTesoreriaForma}
                                      disabled={!puedeMarcarTesoreria || accionando === claveAccionTesoreria}
                                      onChange={() => {
                                        void manejarConciliacionFormaFila(
                                          forma,
                                          "tesoreria",
                                          estadoTesoreriaForma,
                                          claveAccionTesoreria,
                                        );
                                      }}
                                    />
                                  </div>
                                )}
                              </td>
                            </>
                          ) : null}
                          {puedeConciliarSupervisor ? (
                            <td className="text-end align-middle" style={{ width: 140 }}>
                              {esCredito ? (
                                <span className="text-muted">—</span>
                              ) : (
                                <Input
                                  type="number"
                                  bsSize="sm"
                                  value={valorSupervisorTexto}
                                  className="text-end"
                                  disabled={supervisorAprobado}
                                  onChange={(event) =>
                                    setValoresSupervisor((prev) => ({
                                      ...prev,
                                      [forma.id]: event.target.value,
                                    }))
                                  }
                                  onFocus={() => setFuenteDiferencia("supervisor")}
                                />
                              )}
                            </td>
                          ) : null}
                          {puedeConciliarSupervisor ? (
                            <td className="align-middle" style={{ width: 180 }}>
                              <Input
                                type="textarea"
                                rows={1}
                                bsSize="sm"
                                placeholder="Observación Supervisor"
                                className="form-control min-w-150"
                                style={{ resize: "vertical" }}
                                value={observacionSupervisorTexto}
                                disabled={!puedeConciliarSupervisor || supervisorAprobado}
                                onChange={(event) =>
                                  setObservacionesSupervisor((prev) => ({
                                    ...prev,
                                    [claveForma]: event.target.value,
                                  }))
                                }
                              />
                            </td>
                          ) : null}
                          {puedeConciliarSupervisor ? (
                            <td className="text-center align-middle" style={{ width: 120 }}>
                              <div className="form-check form-switch form-switch-success d-inline-flex align-items-center justify-content-center mb-0">
                                <Input
                                  type="checkbox"
                                  role="switch"
                                  className="form-check-input"
                                  id={`switch-supervisor-${rendicionGrupo?.rendicionId ?? "na"}-${claveForma}`}
                                  checked={estadoSupervisorForma}
                                  disabled={!puedeMarcarSupervisor || accionando === claveAccionSupervisor}
                                  onChange={() => {
                                    void manejarConciliacionFormaFila(
                                      forma,
                                      "supervisor",
                                      estadoSupervisorForma,
                                      claveAccionSupervisor,
                                    );
                                  }}
                                />
                              </div>
                            </td>
                          ) : null}
                          <td className="text-end align-middle">
                            {montoCajero != null ? (
                              <span className="fw-semibold">{formatMonto(montoCajero)}</span>
                            ) : (
                              <span className="text-muted">—</span>
                            )}
                          </td>
                          <td className="text-end fw-semibold align-middle">{formatMonto(forma.monto)}</td>
                          <td className="text-end align-middle" style={{ width: 265 }}>
                            {esCredito ? (
                              <span className="text-muted">—</span>
                            ) : (
                              <Badge
                                color={
                                  Math.abs(diferenciaFila) < 1
                                    ? "success"
                                    : diferenciaFila > 0
                                    ? "info"
                                    : "danger"
                                }
                              >
                                {diferenciaFila > 0 ? "+" : ""}
                                {formatMonto(diferenciaFila)}
                              </Badge>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr className="bg-light">
                      <th>Total del grupo</th>
                      {puedeConciliarTesoreria && (
                        <>
                          <th className="text-end">{formatMonto(totalTesoreriaGrupo)}</th>
                          <th />
                          <th />
                        </>
                      )}
                      {puedeConciliarSupervisor && (
                        <>
                          <th className="text-end">{formatMonto(totalSupervisorGrupo)}</th>
                          <th />
                          <th />
                        </>
                      )}
                      <th className="text-end">{formasCajero.length > 0 ? formatMonto(totalCajeroGrupo) : "—"}</th>
                      <th className="text-end">{formatMonto(totalFacturas)}</th>
                      <th className="text-end">
                        {formatMonto(totalFuenteGrupo - totalFacturas)}
                      </th>
                    </tr>
                  </tfoot>
                </Table>
              </div>

              <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mt-3">
                <div className="text-muted small">
                  {filasFacturas.length} movimientos ·{" "}
                  {totalAnuladas > 0 ? `${totalAnuladas} anuladas · ` : ""}
                  {totalCredito > 0 ? `${totalCredito} crédito · ` : ""}
                  {/* placeholder for future info */}
                </div>
                <div className="d-flex flex-wrap gap-2">
                  <Button
                    color="soft-primary"
                    size="sm"
                    className="d-inline-flex align-items-center gap-1 px-3"
                    onClick={() => onVerFacturas?.(contexto)}
                  >
                    <i className="ri-file-list-line" />
                    Ver facturas
                  </Button>
                  <Button
                    color="soft-success"
                    size="sm"
                    className="d-inline-flex align-items-center gap-1 px-3"
                    onClick={() => onAgregarFormaPago?.(contexto)}
                    disabled={!onAgregarFormaPago}
                  >
                    <i className="ri-bank-card-line" />
                    Agregar forma de pago
                  </Button>
                  <Button
                    color="soft-info"
                    size="sm"
                    className="d-inline-flex align-items-center gap-1 px-3"
                    onClick={() => abrirModalPlataformas(contexto)}
                    disabled={!contexto.idcontrolcaja}
                  >
                    <i className="ri-apps-2-line" />
                    Plataformas
                  </Button>
                  <Button
                    color="soft-warning"
                    size="sm"
                    className="d-inline-flex align-items-center gap-1 px-3"
                    onClick={() => abrirDepositoParaCaja(contexto)}
                    disabled={!contexto.idcontrolcaja}
                  >
                    <i className="ri-bank-line" />
                    Registrar depósito
                  </Button>
                  <Button color="light" size="sm" onClick={onReload}>
                    Actualizar
                  </Button>
                </div>
              </div>
            </CardBody>
          </Card>
        );
      })}
      <Modal
        isOpen={Boolean(modalDeposito.caja)}
        toggle={() => setModalDeposito({ caja: null, sucursal: null })}
        size="lg"
        centered
      >
        <ModalHeader toggle={() => setModalDeposito({ caja: null, sucursal: null })}>
          Registrar depósito
        </ModalHeader>
        <ModalBody>
          {modalDeposito.caja ? (
            <DepositoBoletaForm
              cajasDisponibles={[modalDeposito.caja]}
              sucursalesDisponibles={
                modalDeposito.sucursal ? [modalDeposito.sucursal] : []
              }
              cargandoCajas={false}
              errorCajas={null}
              cargandoSucursales={false}
              errorSucursales={null}
              onSalirDeposito={() => setModalDeposito({ caja: null, sucursal: null })}
              cajaFija={modalDeposito.caja}
            />
          ) : null}
        </ModalBody>
      </Modal>
      <Modal
        isOpen={modalPlataformas.visible}
        toggle={cerrarModalPlataformas}
        size="lg"
        centered
      >
        <ModalHeader toggle={cerrarModalPlataformas}>Conciliaciones por plataforma</ModalHeader>
        <ModalBody>
          {modalPlataformas.contexto ? (
            <>
              <Row className="g-3 mb-3">
                <Col md={6}>
                  <Label className="form-label text-muted">Plataforma</Label>
                  <Input
                    type="select"
                    value={plataformaSeleccionada}
                    onChange={(event) =>
                      manejarCambioPlataformaModal(event.target.value as PlataformaIntegracion)
                    }
                  >
                    {PLATAFORMAS_DISPONIBLES.map((opcion) => (
                      <option key={opcion.id} value={opcion.id}>
                        {opcion.label}
                      </option>
                    ))}
                  </Input>
                </Col>
                <Col md={6} className="text-muted small d-flex align-items-end justify-content-md-end">
                  <div>
                    {modalPlataformas.contexto.sucursal} · {modalPlataformas.contexto.caja}
                    <br />
                    Turno {modalPlataformas.contexto.turno}
                  </div>
                </Col>
              </Row>
              {plataformasError && (
                <Alert color="danger" className="mb-3">
                  {plataformasError}
                </Alert>
              )}
              {plataformasCargando ? (
                <div className="text-center text-muted py-4">
                  <Spinner size="sm" className="me-2" /> Cargando conciliaciones…
                </div>
              ) : plataformasDatos && plataformasDatos.registros.length > 0 ? (
                <>
                  <div className="d-flex justify-content-between text-muted small mb-2">
                    <span>Conciliaciones: {plataformasDatos.total}</span>
                    <span>Total monto: {formatMonto(plataformasDatos.totalMonto)}</span>
                  </div>
                  <div className="table-responsive">
                    <Table className="table-sm align-middle">
                      <thead className="table-light">
                        <tr>
                          <th>Registro</th>
                          <th>Movimiento</th>
                          <th>Fecha</th>
                          <th>Sucursales</th>
                          <th>Empresa</th>
                          <th className="text-end">Monto</th>
                          <th>Estado</th>
                          <th>Observación</th>
                          <th>Auditoría</th>
                        </tr>
                      </thead>
                      <tbody>
                        {plataformasDatos.registros.map((registro) => (
                          <tr key={registro.id}>
                            <td>
                              <div className="fw-semibold" title={registro.registroId}>
                                {abreviarIdentificador(registro.registroId)}
                              </div>
                              <div className="text-muted small">
                                Sistema: {registro.sucursalRegistro ?? "Sin dato"}
                              </div>
                            </td>
                            <td>
                              <div className="fw-semibold" title={registro.movimientoId ?? undefined}>
                                {registro.movimientoId
                                  ? abreviarIdentificador(registro.movimientoId)
                                  : "Sin movimiento"}
                              </div>
                              <div className="text-muted small">
                                Plataforma: {registro.sucursalPlataforma ?? "Sin dato"}
                              </div>
                            </td>
                            <td className="text-nowrap" title={formatFecha(registro.fechaDia)}>
                              {formatFechaCorta(registro.fechaDia)}
                            </td>
                            <td>
                              <div className="text-muted small">
                                Sistema: {registro.sucursalRegistro ?? "—"}
                              </div>
                              <div className="text-muted small">
                                Plataforma: {registro.sucursalPlataforma ?? "—"}
                              </div>
                            </td>
                            <td>
                              <div className="fw-semibold">{registro.empresaNombre ?? "—"}</div>
                              <div className="text-muted small">{registro.empresaCodigo ?? ""}</div>
                            </td>
                            <td className="text-end">{formatMonto(registro.monto ?? 0)}</td>
                            <td>
                              {registro.estado ? (
                                <Badge color="info">{registro.estado}</Badge>
                              ) : (
                                <span className="text-muted">—</span>
                              )}
                            </td>
                            <td>
                              {registro.observacion ? (
                                <div className="text-truncate" style={{ maxWidth: 200 }} title={registro.observacion}>
                                  {registro.observacion}
                                </div>
                              ) : (
                                <span className="text-muted">—</span>
                              )}
                            </td>
                            <td>
                              <div className="text-muted small">
                                {registro.usuarioNombre ?? "Sin usuario"}
                                {registro.usuarioId ? ` · ID ${registro.usuarioId}` : ""}
                              </div>
                              <div className="text-muted small">Creado: {formatFecha(registro.creadoEn)}</div>
                              <div className="text-muted small">Actualizado: {formatFecha(registro.actualizadoEn)}</div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                </>
              ) : (
                <div className="text-muted text-center py-4">
                  No encontramos conciliaciones registradas para esta plataforma.
                </div>
              )}
            </>
          ) : (
            <div className="text-muted text-center py-4">Seleccioná una caja para revisar.</div>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={cerrarModalPlataformas}>
            Cerrar
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default ControlCajaPanel;
